import { DollarOutlined, EyeOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Divider, Table, TableProps, Tag } from 'antd';
import { MyModal } from 'components';
import dayjs from 'dayjs';
import { IKeys, IUserRoles, IWork, IWorkState } from 'models';
import { useCallback, useState } from 'react';
import { workService } from 'services';
import { userStore } from 'stores';
import { formatPhoneNumber, statusSAdmin } from 'utils';
import { getWorkNumber } from 'utils/get-work-number';
import { getZipColor } from 'utils/zip';

export const Works = () => {
	const [createQuoteDrawer, setCreateQuoteDrawer] = useState(false);
	const [page, setPage] = useState(1);
	const [filters, setFilters] = useState<{}>(userStore.isManager ? { author: [userStore.data?.id] } : {});
	const { data, isPending } = useQuery({
		queryFn: () => workService.getAll({ page, ...filters }),
		queryKey: [IKeys.WORKS, { page, filters }]
	});

	const [work, setWork] = useState<null | IWork>(null);
	const openWorkDrawer = (values: IWork) => {
		setWork(values);
	};
	const handleCancel = useCallback(() => {
		setWork(null);
		setCreateQuoteDrawer(false);
	}, []);

	const columns: TableProps<IWork>['columns'] = [
		{
			title: '#',
			dataIndex: ['id'],
			key: 'id',
			render: (id, record) => <span onClick={() => openWorkDrawer(record)}>{getWorkNumber(record)}</span>
		},
		{
			title: 'Name',
			dataIndex: ['acf', 'customer_info', 'customer_name'],
			key: 'name',
			render: (name, record) => <span style={{ color: getZipColor(record?.acf?.customer_info?.pickup_address?.[0]?.zip) }} onClick={() => openWorkDrawer(record)}>{name || 'undefined'}</span>
		},
		{
			title: 'Created',
			dataIndex: ['date'],
			key: 'created',
			render: date => dayjs(date).format('MM/DD')
		},
		{
			title: 'Move day',
			dataIndex: ['acf', 'date'],
			key: 'moveDate',
			render: date => dayjs(date).format('MM/DD')
		},
		{
			title: 'Phone',
			dataIndex: ['acf', 'customer_info', 'customer_phone'],
			key: 'phone',
			render: phone => <a style={{ minWidth: 120, display: 'block' }} href={`tel:${phone}`}>{formatPhoneNumber(phone)}</a>
		},
		{
			title: 'Email',
			dataIndex: ['acf', 'customer_info', 'customer_email'],
			key: 'email',
			render: email => <a href={`mailto:${email}`}>{email}</a>
		},
		{
			title: 'Status',
			dataIndex: ['acf', 'state'],
			key: 'state',
			filters: statusSAdmin,
			render: (state: IWorkState, record) => {
				if (state === IWorkState.LOST) {
					return <Tag color="red">Lost</Tag>;
				} else if (state === IWorkState.QUOTE) {
					return <Tag color="gold">Quote {record.acf.watched && <EyeOutlined />}{record.acf.followups?.length}</Tag>;
				} else if (state === IWorkState.CONFIRMED) {
					return <Tag color="green">Confirmed {record.acf.paid && <DollarOutlined />}</Tag>;
				} else if (state === IWorkState.COMPLETED) {
					return <Tag color="geekblue">Completed</Tag>;
				} else if (state === IWorkState.ASSIGN_WORKERS) {
					return <Tag color="blue">In progress</Tag>;
				} else if (state === IWorkState.PENDING) {
					return <Tag color="volcano">Pending</Tag>;
				}
			}
		},
		{
			title: 'Manager',
			dataIndex: ['author'],
			key: 'author',
			render: id => userStore.getUser(id)?.name,
			filters: userStore.isAdmin ? userStore.staff.map(user => ({ text: user.name, value: user.id })) : undefined,
			defaultFilteredValue: userStore.isManager ? [userStore.data?.id?.toString() || ''] : []
		}
	];

	return (
		<>
			{(userStore.role === IUserRoles.ADMIN || userStore.role === IUserRoles.MANAGER) && (
				<>
					<Button onClick={() => setCreateQuoteDrawer(true)} type="primary" danger block>
						Create quote
					</Button>
					<Divider size="small" />
					<Table<IWork>
						loading={isPending}
						rowKey={record => record.id}
						columns={columns}
						dataSource={data?.data}
						size="small"
						onChange={(_, filters) => {
							setFilters(filters);
						}}
						pagination={{
							current: page,
							pageSize: 10,
							total: data?.headers['x-wp-total'],
							onChange: (page) => setPage(page),
							showSizeChanger: false
						}}
					/>
					<MyModal
						work={work}
						handleCancel={handleCancel}
						queryKey={[IKeys.WORKS, { page, filters }]}
						open={createQuoteDrawer}
					/>
				</>
			)}
		</>
	);
};
